import { Icon } from '@iconify/react';
import { Box, Stack, TableCell, TableRow, Typography, Popover, MenuList, MenuItem, ListItemIcon, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import { useState, useEffect } from 'react';
import ReactLoading from 'react-loading';
import { useNavigate } from 'react-router-dom';
import { SelectionSet } from 'aws-amplify/api';
import { type Schema } from '../../../../../../amplify/data/resource';
import FileDetails from './FileDetails';
import GenerateFile from './Dialogs/GenerateFile.tsx/GenerateFile';
import TransferFile from './Dialogs/TransferFile/TransferFile';
import awsConfig from '../../../../../amplify_outputs.json';
import { useSnackbar } from 'notistack';
import { generateClient } from 'aws-amplify/data';
import Button from '../../../../../components/custom/Button';
import SalaryMonthsDisplay from '../../../../../components/custom/SalaryMonthsDisplay';
import EmployeesList from '../../../Employees/EmployeesList/EmployeesList';
import { useAuth } from '../../../../../utils/context/useAuthContext';

const apiClient = generateClient<Schema>();

const selectionSet = [
  'id',
  'path',
  'date',
  'createdAt',
  'status',
  'error',
  'type',
  'xmlVersion',
  'executionDate',
  'workEnvironment',
  'fileNumber',
  'fileName',
  'sequenceNumber',
  'senderCode',
  'senderIdentifierType',
  'senderId',
  'senderName',
  'senderContactFirstName',
  'senderContactLastName',
  'senderContactPhone',
  'senderContactEmail',
  'senderContactCellular',
  'recipientCode',
  'recipientIdentifierType',
  'recipientId',
  'recipientCreatorIdentifier',
  'userId',
  'user.*',
  'deposits.totalDepositAmount',
  'deposits.fundIdentifier',
  'deposits.employees.employeeId',
  'deposits.employees.salaryMonth',
] as const;

// @ts-ignore
type File = SelectionSet<Schema['File']['type'], typeof selectionSet>;

// @ts-ignore
type Employee = Schema['Employee']['type'];

interface FileItemProps {
  file: File;
}

const getFileIcon = (status: String) => {
  if (status === 'COMPLETED') {
    return '/assets/dashboard/fileGreen.svg';
  } else if (status === 'IN_PROGRESS') {
    return '/assets/dashboard/fileYellow.svg';
  } else if (status === 'PENDING') {
    return '/assets/dashboard/fileGrey.svg';
  }
  else if (status === 'PAID') {
    return '/assets/dashboard/fileYellow.svg';
  }  else if (status === 'REJECTED') {
    return '/assets/dashboard/fileRed.svg';
  }
};

const getFileIconTextColor = (status: String) => {
  if (status === 'COMPLETED') {
    return '#94C729';
  } else if (status === 'IN_PROGRESS') {
    return '#F6B209';
  } else if (status === 'PENDING') {
    return '#808080';
  } else if (status === 'REJECTED') {
    return '##FF6F78';
  }
};

const FileItem: React.FC<FileItemProps> = ({ file }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const [openDetails, setOpenDetails] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openGenerateFile, setOpenGenerateFile] = useState(false);
  const [openTransferFile, setOpenTransferFile] = useState(false);
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openEmployeesList, setOpenEmployeesList] = useState(false);
  const [employeesData, setEmployeesData] = useState<Employee[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const handleToggleDetails = () => {
    setOpenDetails((prev) => !prev);
  };

  const handleToggleEmployeesList = () => {
    setOpenEmployeesList((prev) => !prev);
  };

  // Загружаем employees для этого файла через API
  useEffect(() => {
    if (openEmployeesList && !loadingEmployees && user?.userId) {
      const fetchEmployees = async () => {
        setLoadingEmployees(true);
        try {
          // Загружаем employees связанных с этим файлом
          const { data: fetchedEmployeesData, errors } =
            await apiClient.models.Employee.listEmployeeByFileId({
              fileId: file.id,
            }, {
              filter: {
                and: [
                  {
                    or: [
                      { status: { eq: 'IN_PROGRESS' } },
                      { status: { eq: 'RECORD_CAPTURED_SUCCESS' } }
                    ]
                  },
                ]
              }
            });

          if (errors && errors.length > 0) {
            console.error('Errors fetching employees:', errors);
          }

          const currentEmployees = fetchedEmployeesData || [];
          setEmployeesData([...currentEmployees]);
        } catch (error) {
          console.error('Exception during fetching employees:', error);
        } finally {
          setLoadingEmployees(false);
        }
      };

      fetchEmployees();
    }
  }, [openEmployeesList, user?.userId, file.id]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleUploadFile = () => {
    setOpenTransferFile(true);
    handleMenuClose();
  };

  const handleDownloadLastFile = async () => {
    try {
      const downloadResponse = await apiClient.queries.downloadLastFile({
        fileId: file.id,
      });

      if (downloadResponse?.data) {
        // First JSON parse - get the outer response
        const outerResponse = JSON.parse(downloadResponse.data as string);
        
        if (outerResponse.statusCode === 200) {
          // Second JSON parse - get the actual data
          const responseBody = JSON.parse(outerResponse.body);
          
          if (responseBody.success) {
            // Extract filename from filePath and replace extension with .DAT
            const originalFileName = responseBody.filePath.split('/').pop() || 'downloaded-file';
            const fileNameWithoutExt = originalFileName.replace(/\.[^/.]+$/, ''); // Remove extension
            const fileName = `${fileNameWithoutExt}.DAT`;
            
            // Create and download the file
            const blob = new Blob([responseBody.content], { type: 'application/xml' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            enqueueSnackbar('קובץ אחרון הורד בהצלחה', { variant: 'success' });
          } else {
            enqueueSnackbar(`שגיאה בהורדת הקובץ: ${responseBody.error || 'שגיאה לא ידועה'}`, { variant: 'error' });
          }
        } else {
          // Handle HTTP error status
          const errorBody = JSON.parse(outerResponse.body);
          enqueueSnackbar(`שגיאה בהורדת הקובץ: ${errorBody.error || 'שגיאה לא ידועה'}`, { variant: 'error' });
        }
      } else {
        enqueueSnackbar('שגיאה בהורדת הקובץ: לא התקבל מידע', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error downloading last file:', error);
      enqueueSnackbar('שגיאה בהורדת הקובץ', { variant: 'error' });
    }
    
    handleMenuClose();
  };

  const handleDownloadExcel = () => {
    enqueueSnackbar('הורדת קובץ אקסל', { variant: 'info' });
    handleMenuClose();
  };

  const handleEnterReference = () => {
    setOpenGenerateFile(true);
    handleMenuClose();
  };

  const handleCloseGenerateFile = () => {
    setOpenGenerateFile(false);
  };

  const handleCloseTransferFile = () => {
    setOpenTransferFile(false);
  };

  const handleResetStatus = () => {
    setOpenResetDialog(true);
    handleMenuClose();
  };

  const handleCloseResetDialog = () => {
    setOpenResetDialog(false);
  };

  const handleConfirmResetStatus = async () => {
    try {
      await apiClient.models.File.update({
        id: file.id,
        status: 'PENDING'
      });
      enqueueSnackbar('סטטוס הקובץ אופס בהצלחה', { variant: 'success' });
      setOpenResetDialog(false);
      window.location.reload();
    } catch (error) {
      enqueueSnackbar('שגיאה באיפוס סטטוס הקובץ', { variant: 'error' });
      console.error('Error resetting file status:', error);
    }
  };

  const handleSetToPaid = async () => {
    try {
      await apiClient.models.File.update({
        id: file.id,
        status: 'PAID'
      });
      enqueueSnackbar('סטטוס הקובץ עודכן לשולם בהצלחה', { variant: 'success' });
      handleMenuClose();
      window.location.reload();
    } catch (error) {
      enqueueSnackbar('שגיאה בעדכון סטטוס הקובץ', { variant: 'error' });
      console.error('Error updating file status to PAID:', error);
    }
  };

  const handleDeleteFile = () => {
    setOpenDeleteDialog(true);
    handleMenuClose();
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleConfirmDeleteFile = async () => {
    try {
      await apiClient.models.File.delete({
        id: file.id
      });
      enqueueSnackbar('הקובץ נמחק בהצלחה', { variant: 'success' });
      setOpenDeleteDialog(false);
      window.location.reload();
    } catch (error) {
      enqueueSnackbar('שגיאה במחיקת הקובץ', { variant: 'error' });
      console.error('Error deleting file:', error);
    }
  };

  const totalToBePaid = Array.isArray(file?.deposits)
    ? file.deposits.reduce(
        (accumulator: number, deposit: { totalDepositAmount: number | null }) => {
          return accumulator + (deposit.totalDepositAmount || 0);
        },
        0,
      )
    : 0;

  const employees: Employee[] = Array.isArray(file?.deposits)
    ? file.deposits
        .flatMap((deposit: any) => deposit.employees || [])
        .reduce((uniqueEmployees: Employee[], currentEmployee: Employee) => {
          if (
            !uniqueEmployees.some(
              (e) => e.employeeId === currentEmployee.employeeId,
            )
          ) {
            uniqueEmployees.push(currentEmployee);
          }
          return uniqueEmployees;
        }, [])
    : [];

  const numberOfDeposits = file?.deposits?.length?.toString().padStart(2, '0');

  const fileNavigate = (fileId: string) => {
    navigate(`/file-overview?fileId=${fileId}`);
  };

  const handleDownload = async () => {
    if (file) {
      const bucketName = awsConfig.storage.bucket_name;
      const fileKey = `files/${file.id}`;

      try {
        const downloadResponse = await apiClient.queries.downloadFile({
          path: fileKey,
          bucketName: bucketName,
        });

        if (downloadResponse?.data) {
          // Parse the JSON response
          const responseData = typeof downloadResponse.data === 'string' 
            ? JSON.parse(downloadResponse.data) 
            : downloadResponse.data;

          if (responseData?.body) {
            // Convert base64 to blob
            const base64Data = responseData.body;
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { 
              type: responseData.headers?.['Content-Type'] || 'application/octet-stream' 
            });

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.fileName || responseData.metadata?.fileName || 'file';
            a.click();
            window.URL.revokeObjectURL(url);

            enqueueSnackbar('הקובץ ירד בהצלחה!', { variant: 'success' });
          } else {
            throw new Error('לא התקבל תוכן קובץ');
          }
        } else {
          throw new Error('לא התקבל תוכן קובץ');
        }
      } catch (error) {
        console.error('Error downloading file:', error);
        enqueueSnackbar('שגיאה בהורדת הקובץ', { variant: 'error' });
      }
    } else {
      enqueueSnackbar('לא ניתן להוריד את הקובץ', { variant: 'error' });
    }
  };

  const getPaymentStatusText = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'טרם נשלח';
      case 'IN_PROGRESS':
        return 'בתהליך';
      case 'PAID':
        return 'הושלם';
      case 'COMPLETED':
        return 'הושלם';
    }
  };

  const getMislakaStatusText = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'טרם נשלח';
        case 'IN_PROGRESS':
          return 'טרם נשלח';
      case 'PAID':
        return 'בתהליך';
      case 'COMPLETED':
        return 'אושר';
    }
  };

  return (
    <>
      {file.status === 'PROCESSING' ? (
        <TableRow
          sx={{
            backgroundColor: '#F2F4F7',
            borderRadius: '10px',
            overflow: 'hidden',
            '&:last-child td, &:last-child th': { border: 0 },
            boxShadow: 'none',
            textAlign: 'center',
            verticalAlign: 'middle',
            cursor: 'default',
          }}
        >
          <TableCell
            colSpan={8}
            sx={{ textAlign: 'center', borderRadius: '10px' }}
          >
            <Stack
              justifyContent="center"
              alignItems="center"
              spacing={2}
              sx={{ py: 0 }}
            >
              <ReactLoading
                type={'bubbles'}
                color={'#94C729'}
                height={30}
                width={50}
              />
              <Typography
                fontWeight="500"
                fontSize="16px"
                fontFamily="Heebo"
                color="#94C729"
              >
                עיבוד קובץ של{' '}
                {format(new Date(file.date), 'MMMM', { locale: he })}{' '}
                {format(new Date(file.date), 'yyyy', { locale: he })}
              </Typography>
            </Stack>
          </TableCell>
        </TableRow>
      ) : file.status === 'ERROR' ? (
        <TableRow
          sx={{
            backgroundColor: '#FDF2F2',
            borderRadius: '10px',
            overflow: 'hidden',
            '&:last-child td, &:last-child th': { border: 0 },
            boxShadow: 'none',
            textAlign: 'center',
            verticalAlign: 'middle',
            cursor: 'default',
          }}
        >
          <TableCell
            colSpan={8}
            sx={{ textAlign: 'center', borderRadius: '10px' }}
          >
            <Stack
              justifyContent="center"
              alignItems="center"
              spacing={0.5}
              sx={{ py: 0 }}
            >
              <Icon
                icon="mdi:close-circle-outline"
                color="#FF6F78"
                width={40}
                height={40}
              />
              <Typography
                fontWeight="500"
                fontSize="16px"
                fontFamily="Heebo"
                color="#FF6F78"
              >
                הקובץ של {format(new Date(file.date), 'MMMM', { locale: he })}{' '}
                {format(new Date(file.date), 'yyyy', { locale: he })} אינו תואם
                את הפורמט
              </Typography>
            </Stack>
          </TableCell>
        </TableRow>
      ) : (
        <TableRow
          sx={{
            backgroundColor: '#F2F4F7',
            borderRadius: '10px',
            p: '20px',
            overflow: 'hidden',
            '&:last-child td, &:last-child th': { border: 0 },
            boxShadow: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <TableCell
            sx={{
              padding: '20px',
              borderBottom: 'none',
              borderTopRightRadius: '10px',
              borderBottomRightRadius: '10px',
            }}
          >
            <Box
              sx={{
                textAlign: 'right',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                gap: '2px',
              }}
            >
              <Typography
                fontWeight={'400'}
                fontSize={'12px'}
                fontFamily={'Montserrat'}
              >
                {format(new Date(file.createdAt), 'dd/MM/yyyy', { locale: he })}
              </Typography>
            </Box>
          </TableCell>

          <TableCell
            sx={{
              py: '20px',
              px: '10px',
              borderBottom: 'none',
              borderTopRightRadius: '10px',
              borderBottomRightRadius: '10px',
            }}
          >
            <Box
              sx={{
                textAlign: 'right',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                gap: '2px',
              }}
            >
              <SalaryMonthsDisplay employees={employees} />
            </Box>
          </TableCell>

          <TableCell
            sx={{
              padding: '20px',
              borderBottom: 'none',
              textAlign: 'right',
              verticalAlign: 'bottom',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '8px', cursor: 'pointer' }} onClick={() => fileNavigate(file.id)}>
              <img
                alt="file icon"
                src={getFileIcon(file.status || 'PENDING')}
              />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  textAlign: 'right',
                }}
              >
                <Typography
                  fontWeight={'500'}
                  fontSize={'20px'}
                  fontFamily={'Montserrat'}
                  color={getFileIconTextColor(file.status || 'PENDING')}
                >
                  {numberOfDeposits}
                </Typography>
                <Typography
                  fontWeight={'700'}
                  fontSize={'14px'}
                  color={getFileIconTextColor(file.status || 'PENDING')}
                  fontFamily={'AlmoniDL'}
                >
                  הוראות תשלום
                </Typography>
              </Box>
            </Box>
          </TableCell>

          <TableCell
            sx={{
              padding: '20px',
              borderBottom: 'none',
              textAlign: 'right',
              verticalAlign: 'bottom',
            }}
          >
            <Typography
              fontWeight={'700'}
              fontSize={'14px'}
              fontFamily={'AlmoniDL'}
            >
              {file?.senderName && file.senderName.length > 10
                ? `${file.senderName.slice(0, 10)}...`
                : file?.senderName}
            </Typography>
          </TableCell>

          <TableCell
            sx={{
              padding: '20px',
              borderBottom: 'none',
              textAlign: 'right',
              verticalAlign: 'bottom',
            }}
          >
            <Box onClick={() => navigate(`/employees?fileId=${file.id}`)} sx={{ display: 'flex', alignItems: 'flex-end', gap: '8px', cursor: 'pointer' }}>
              <img alt="card" src={'/assets/dashboard/card.svg'} />
              <Typography
                fontWeight={'500'}
                fontSize={'20px'}
                fontFamily={'Montserrat'}
              >
                {employees?.length?.toString()?.padStart(2, '0')}
              </Typography>
            </Box>
          </TableCell>

          <TableCell
            sx={{
              padding: '20px',
              borderBottom: 'none',
              textAlign: 'right',
              verticalAlign: 'bottom',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
              <img src={'/assets/dashboard/coins.svg'} alt="coins" />
              <Typography
                fontWeight={'500'}
                fontSize={'20px'}
                fontFamily={'Montserrat'}
              >
                {totalToBePaid.toLocaleString()}
              </Typography>
            </Box>
          </TableCell>

          <TableCell
            sx={{
              padding: '20px',
              borderBottom: 'none',
              textAlign: 'right',
              verticalAlign: 'bottom',
            }}
          >
            <Typography
              fontWeight={'700'}
              fontSize={'14px'}
              fontFamily={'AlmoniDL'}
            >
              {getPaymentStatusText(file.status || 'PENDING')}
            </Typography>
          </TableCell>

          <TableCell
            sx={{
              padding: '20px',
              borderBottom: 'none',
              textAlign: 'right',
              verticalAlign: 'bottom',
            }}
          >
            <Typography
              fontWeight={'700'}
              fontSize={'14px'}
              fontFamily={'AlmoniDL'}
            >
              {getMislakaStatusText(file.status || 'PENDING')}
            </Typography>
          </TableCell>

          <TableCell
            sx={{
              padding: '20px',
              borderBottom: 'none',
              textAlign: 'right',
              borderTopLeftRadius: '10px',
              borderBottomLeftRadius: '10px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '45px',
                  height: '45px',
                  background: 'white',
                  borderRadius: '10px',
                  cursor: 'pointer',
                }}
                onClick={handleDownload}
              >
                <img alt="download" src={'/assets/dashboard/download.svg'} />
              </Box>
              <Tooltip title='פירוט עובדים' slotProps={{
                tooltip: {
                  sx: {
                    fontFamily: 'AlmoniDL',
                    fontSize: '12px',
                    fontWeight: '700',
                  },
                },
              }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '45px',
                    height: '45px',
                    background: 'white',
                    borderRadius: '10px',
                    cursor: 'pointer',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleEmployeesList();
                  }}
                >
                  <Icon
                    icon={openEmployeesList ? "mdi:chevron-down" : "mdi:chevron-up"}
                    width={20}
                    height={20}
                    color="#666"
                  />
                </Box>
              </Tooltip>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '45px',
                  height: '45px',
                  background: 'white',
                  borderRadius: '10px',
                  cursor: 'pointer',
                }}
                onClick={handleToggleDetails}
              >
                <img
                  alt="open"
                  src={'/assets/dashboard/open.svg'}
                  style={{
                    transition: 'transform 0.3s ease-in-out',
                    transform: openDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '45px',
                  height: '45px',
                  background: 'white',
                  borderRadius: '10px',
                  cursor: 'pointer',
                }}
                onClick={handleMenuClick}
              >
                <Icon
                  icon="mdi:dots-horizontal"
                  width={20}
                  height={20}
                  color="#666"
                />
              </Box>
            </Box>
          </TableCell>
        </TableRow>
      )}
      <TableRow>
        <TableCell colSpan={9} sx={{ padding: 0 }}>
          <FileDetails open={openDetails} fileId={file.id} />
        </TableCell>
      </TableRow>
      
      {/* Expandable row for EmployeesList */}
      {openEmployeesList && file.status !== 'PROCESSING' && file.status !== 'ERROR' && (
        <TableRow>
          <TableCell
            colSpan={9}
            sx={{
              borderBottom: 'none',
              backgroundColor: 'transparent',
              padding: 0
            }}
          >
            <Box sx={{
              backgroundColor: 'transparent',
              margin: '0 20px 20px 20px',
              borderRadius: '10px',
              padding: '20px'
            }}>
              {loadingEmployees ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <ReactLoading
                    type={'bubbles'}
                    color={'#94C729'}
                    height={50}
                    width={100}
                  />
                </Box>
              ) : employeesData.length > 0 ? (
                <EmployeesList employees={employeesData} />
              ) : (
                <Typography
                  textAlign="center"
                  fontFamily={'AlmoniDL'}
                  color="#666"
                  py={4}
                >
                  אין שיוכים להצגה עבור קובץ זה
                </Typography>
              )}
            </Box>
          </TableCell>
        </TableRow>
      )}
      
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '12px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
            minWidth: '280px',
            mt: 1,
          },
        }}
      >
        <MenuList sx={{ py: 1 }}>
          <MenuItem onClick={handleUploadFile} sx={{ py: 1.5, px: 2 }}>
            <ListItemIcon sx={{ minWidth: '36px' }}>
              <Icon icon="mdi:upload" width={20} height={20} color="black" />
            </ListItemIcon>
            <ListItemText 
              primary="העלה קובץ לשליחה למסלקה"
              sx={{ 
                '& .MuiTypography-root': { 
                  fontFamily: 'AlmoniDL',
                  fontSize: '14px',
                  fontWeight: 700,
                  textAlign: 'right'
                } 
              }}
            />
          </MenuItem>
          
          <MenuItem onClick={handleDownloadLastFile} sx={{ py: 1.5, px: 2 }}>
            <ListItemIcon sx={{ minWidth: '36px' }}>
              <Icon icon="mdi:download" width={20} height={20} color="black" />
            </ListItemIcon>
            <ListItemText 
              primary="הורד קובץ אחרון שנשלח למסלקה"
              sx={{ 
                '& .MuiTypography-root': { 
                  fontFamily: 'AlmoniDL',
                  fontSize: '14px',
                  fontWeight: 700,
                  textAlign: 'right'
                } 
              }}
            />
          </MenuItem>
          
          <MenuItem onClick={handleDownloadExcel} sx={{ py: 1.5, px: 2 }}>
            <ListItemIcon sx={{ minWidth: '36px' }}>
              <Icon icon="mdi:file-excel" width={20} height={20} color="black" />
            </ListItemIcon>
            <ListItemText 
              primary="הורד קובץ אקסל"
              sx={{ 
                '& .MuiTypography-root': { 
                  fontFamily: 'AlmoniDL',
                  fontSize: '14px',
                  fontWeight: 700,
                  textAlign: 'right'
                } 
              }}
            />
          </MenuItem>
          
          <MenuItem onClick={handleEnterReference} sx={{ py: 1.5, px: 2 }}>
            <ListItemIcon sx={{ minWidth: '36px' }}>
              <Icon icon="mdi:file-document-edit" width={20} height={20} color="black" />
            </ListItemIcon>
            <ListItemText 
              primary="הזן אסמכתה ושלח למסלקה"
              sx={{ 
                '& .MuiTypography-root': { 
                  fontFamily: 'AlmoniDL',
                  fontSize: '14px',
                  fontWeight: 700,
                  textAlign: 'right'
                } 
              }}
            />
          </MenuItem>
          
          <MenuItem 
            onClick={handleResetStatus} 
            disabled={file.status !== 'IN_PROGRESS'}
            sx={{ 
              py: 1.5, 
              px: 2,
              opacity: file.status !== 'IN_PROGRESS' ? 0.5 : 1
            }}
          >
            <ListItemIcon sx={{ minWidth: '36px' }}>
              <Icon 
                icon="mdi:refresh" 
                width={20} 
                height={20} 
                color={file.status !== 'IN_PROGRESS' ? "#ccc" : "black"} 
              />
            </ListItemIcon>
            <ListItemText 
              primary="איפוס סטטוס"
              sx={{ 
                '& .MuiTypography-root': { 
                  fontFamily: 'AlmoniDL',
                  fontSize: '14px',
                  fontWeight: 700,
                  textAlign: 'right',
                  color: file.status !== 'IN_PROGRESS' ? '#ccc' : 'inherit'
                } 
              }}
            />
          </MenuItem>
          
          <MenuItem 
            onClick={handleSetToPaid} 
            disabled={file.status === 'PAID'}
            sx={{ 
              py: 1.5, 
              px: 2,
              opacity: file.status === 'PAID' ? 0.5 : 1
            }}
          >
            <ListItemIcon sx={{ minWidth: '36px' }}>
              <Icon 
                icon="mdi:check-circle" 
                width={20} 
                height={20} 
                color={file.status === 'PAID' ? "#ccc" : "black"} 
              />
            </ListItemIcon>
            <ListItemText 
              primary="Set to PAID"
              sx={{ 
                '& .MuiTypography-root': { 
                  fontFamily: 'AlmoniDL',
                  fontSize: '14px',
                  fontWeight: 700,
                  textAlign: 'right',
                  color: file.status === 'PAID' ? '#ccc' : 'inherit'
                } 
              }}
            />
          </MenuItem>
          
          <MenuItem onClick={handleDeleteFile} sx={{ py: 1.5, px: 2 }}>
            <ListItemIcon sx={{ minWidth: '36px' }}>
              <Icon icon="mdi:delete" width={20} height={20} color="red" />
            </ListItemIcon>
            <ListItemText 
              primary="מחק קובץ"
              sx={{ 
                '& .MuiTypography-root': { 
                  fontFamily: 'AlmoniDL',
                  fontSize: '14px',
                  fontWeight: 700,
                  textAlign: 'right',
                  color: 'red'
                } 
              }}
            />
          </MenuItem>
        </MenuList>
      </Popover>

      <Dialog
        open={openResetDialog}
        onClose={handleCloseResetDialog}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '12px',
            minWidth: '400px',
            textAlign: 'center',
          },
        }}
      >
        <DialogTitle sx={{ 
          fontFamily: 'AlmoniDL', 
          fontSize: '20px', 
          fontWeight: 700,
          textAlign: 'center',
          pb: 1
        }}>
          איפוס סטטוס
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pt: 1 }}>
          <Typography dir='rtl' sx={{ 
            fontFamily: 'AlmoniDL', 
            fontSize: '16px',
            fontWeight: 700,
            color: 'black'
          }}>
            האם אתה בטוח שברצונך לאפס את סטטוס הקובץ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
          
          <Button
            onClick={handleConfirmResetStatus}
            customVariant="default"
            sx={{
              fontFamily: 'AlmoniDL',
              fontWeight: 700,
              borderRadius: '8px',
              px: 3,
            }}
          >
            אישור
          </Button>
          <Button
            onClick={handleCloseResetDialog}
            customVariant="light"
            sx={{
              fontFamily: 'AlmoniDL',
              fontWeight: 700,
              borderRadius: '8px',
              px: 3,
            }}
          >
            ביטול
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '12px',
            minWidth: '400px',
            textAlign: 'center',
          },
        }}
      >
        <DialogTitle sx={{ 
          fontFamily: 'AlmoniDL', 
          fontSize: '20px', 
          fontWeight: 700,
          textAlign: 'center',
          pb: 1,
          color: 'red'
        }}>
          מחיקת קובץ
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pt: 1 }}>
          <Typography dir='rtl' sx={{ 
            fontFamily: 'AlmoniDL', 
            fontSize: '16px',
            fontWeight: 700,
            color: 'black'
          }}>
            האם אתה בטוח שברצונך למחוק את הקובץ?
          </Typography>
          <Typography dir='rtl' sx={{ 
            fontFamily: 'AlmoniDL', 
            fontSize: '14px',
            fontWeight: 500,
            color: 'red',
            mt: 1
          }}>
            פעולה זו לא ניתנת לביטול
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
          <Button
            onClick={handleConfirmDeleteFile}
            customVariant="default"
            sx={{
              fontFamily: 'AlmoniDL',
              fontWeight: 700,
              borderRadius: '8px',
              px: 3,
              backgroundColor: 'red',
              '&:hover': {
                backgroundColor: 'darkred',
              }
            }}
          >
            מחק
          </Button>
          <Button
            onClick={handleCloseDeleteDialog}
            customVariant="light"
            sx={{
              fontFamily: 'AlmoniDL',
              fontWeight: 700,
              borderRadius: '8px',
              px: 3,
            }}
          >
            ביטול
          </Button>
        </DialogActions>
      </Dialog>

      <GenerateFile
        fileId={file.id}
        amount={totalToBePaid}
        employees={employees.length}
        deposits={file?.deposits?.length || 0}
        depositsArray={file.deposits || []}
        open={openGenerateFile}
        onClose={handleCloseGenerateFile}
      />

      <TransferFile
        fileId={file.id}
        open={openTransferFile}
        onClose={handleCloseTransferFile}
      />
    </>
  );
};

export default FileItem;
